/**
 * In-app wallet — balance is server-authoritative (Firestore users.walletBalance).
 * Client holds a read-only mirror synced from profile; mutations go through walletApi.
 */
import { escrowPayoutAmount } from '../utils/challengeWinner';

export const DEFAULT_WALLET_BALANCE = 0;
export const MAX_WAGER_PER_PLAYER = 500;
export const ADMIN_MAX_LIVE_BALANCE = MAX_WAGER_PER_PLAYER;
export const WALLET_DEPOSIT_PRESETS = [10, 25, 50, 100];
export const MIN_DEPOSIT_DOLLARS = 10;
export const MAX_DEPOSIT_DOLLARS = 250;

export function roundMoney(amount) {
  return Math.round(Number(amount) * 100) / 100;
}

/**
 * Parse withdraw amount from UI input — "15" and "15.00" both yield 15.
 * @returns {{ ok: true, amount: number } | { ok: false, reason: 'empty' | 'invalid' }}
 */
export function parseWithdrawDollarsInput(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return { ok: false, reason: 'empty' };
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return { ok: false, reason: 'invalid' };
  return { ok: true, amount: roundMoney(n) };
}

export function walletStorageKey(uid) {
  return uid ? `lockedin_wallet_mirror_${uid}` : 'lockedin_wallet_mirror_guest';
}

/** @returns {{ balance: number }} */
export function createDefaultWalletState() {
  return { balance: DEFAULT_WALLET_BALANCE };
}

export function walletFromProfile(profileDoc) {
  if (!profileDoc) return createDefaultWalletState();
  return { balance: roundMoney(profileDoc.walletBalance ?? DEFAULT_WALLET_BALANCE) };
}

export function mapWalletToUserFields(state) {
  const disabled = !!state?.walletDisabled;
  return {
    walletBalance: disabled ? 0 : roundMoney(state?.balance ?? 0),
    walletDisabled: disabled,
  };
}

export function loadWallet(uid, profileDoc) {
  if (profileDoc) return walletFromProfile(profileDoc);
  try {
    const raw = window.localStorage.getItem(walletStorageKey(uid));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed?.balance === 'number') {
        return { balance: roundMoney(parsed.balance), lastUpdated: parsed.lastUpdated ?? null };
      }
    }
  } catch (_) {}
  return createDefaultWalletState();
}

export function saveWalletMirror(uid, state) {
  try {
    window.localStorage.setItem(
      walletStorageKey(uid),
      JSON.stringify({ balance: roundMoney(state.balance), lastUpdated: Date.now() })
    );
  } catch (_) {}
}

/** Remove guest wallet mirror — call before auth→guest transitions. */
export function clearGuestWalletMirror() {
  try {
    window.localStorage.removeItem(walletStorageKey(null));
  } catch (_) {}
}

/** Sync local mirror from authoritative profile — never writes balance to Firestore. */
export function syncWalletFromProfile(state, profileDoc) {
  const fromServer = walletFromProfile(profileDoc);
  return { ...state, balance: fromServer.balance, walletDisabled: state?.walletDisabled };
}

export function getActiveWallet(state) {
  return state || createDefaultWalletState();
}

export function getWalletAccountMode() {
  return 'wallet';
}

export function isLiveWalletMode() {
  return true;
}

export function hasLiveAccountAccess() {
  return false;
}

export function hasFullPlatformAccess() {
  return false;
}

export const LIVE_ACCOUNT_LOCKED_MESSAGE =
  'Subscribe to unlock premium features. Wallet funds are separate from subscriptions.';

export const FREE_TIER_WALLET_MESSAGE =
  'Add funds to your wallet for head-to-head challenges.';

export function canAfford(balance, stake) {
  return roundMoney(balance) >= roundMoney(stake);
}

export function isValidWagerStake(stake) {
  const n = roundMoney(stake);
  return Number.isFinite(n) && n > 0;
}

export function isWithinMaxWager(stake, max = MAX_WAGER_PER_PLAYER) {
  return roundMoney(stake) <= roundMoney(max);
}

export function maxWagerExceededMessage(max = MAX_WAGER_PER_PLAYER) {
  return `Max challenge amount is $${max.toFixed(2)} per player. Lower your entry to continue.`;
}

export function insufficientFundsMessage(stake, balance) {
  return `Not enough funds. You need $${roundMoney(stake).toFixed(2)} but only have $${roundMoney(balance).toFixed(2)}. Add funds in Wallet.`;
}

export function deductStake(wallet, stake) {
  const s = roundMoney(stake);
  if (s <= 0 || wallet.balance < s) return null;
  return { ...wallet, balance: roundMoney(wallet.balance - s) };
}

export function creditStake(wallet, stake) {
  const s = roundMoney(stake);
  if (s <= 0) return wallet;
  return { ...wallet, balance: roundMoney(wallet.balance + s) };
}

export function mutateActiveWallet(state, mutator) {
  const next = mutator(state);
  if (!next) return null;
  return next;
}

export function lockRecipientStake(state, stake) {
  return mutateActiveWallet(state, (w) => deductStake(w, stake));
}

export function applyBetSettlementToState(state, bet, resolution, userId) {
  return {
    ...state,
    balance: applyBetSettlement(state, bet, resolution, userId).balance,
  };
}

export function applyBetSettlement(wallet, bet, resolution, userId) {
  if (!resolution || resolution.isDraw || !bet?.participants?.length) return wallet;
  const winnerId = resolution.winningPlayerId;
  if (!winnerId) return wallet;
  if (winnerId === userId) {
    return creditStake(wallet, resolution.payoutAmount ?? escrowPayoutAmount(bet.totalPot));
  }
  return wallet;
}

export function refundParticipantStakeOnState(state, bet, userId) {
  return {
    ...state,
    balance: refundParticipantStake(state, bet, userId).balance,
  };
}

export function refundParticipantStake(wallet, bet, userId) {
  const p = bet.participants?.find((x) => x.id === userId);
  if (!p?.amount) return wallet;
  return creditStake(wallet, p.amount);
}

// Legacy stubs — demo/live split removed
export function setWalletActiveMode(state) {
  return state;
}
export function persistWallet(uid, state) {
  saveWalletMirror(uid, state);
  return Promise.resolve();
}
export function depositToLiveWallet(state, amount) {
  return creditStake(state, amount);
}
export function addFundsToLive(state, amount) {
  return creditStake(state, amount);
}
export function linkBankOnLive(state) {
  return state;
}
export function linkBankDemo(state) {
  return state;
}
export function addFunds(state, amount) {
  return creditStake(state, amount);
}
export function resetDemoWalletSlice(slice) {
  return slice;
}
export function capDemoWalletBalance(slice) {
  return slice;
}
export function capLiveWalletBalance(slice) {
  return slice;
}
